import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight,
  Users, Plus
} from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const LEAVE_COLORS = {
  sick_leave: { bg: 'bg-rose-100', dot: 'bg-rose-500', text: 'text-rose-600', light: 'bg-rose-50' },
  casual_leave: { bg: 'bg-blue-100', dot: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50' },
  vacation: { bg: 'bg-amber-100', dot: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50' },
  maternity_leave: { bg: 'bg-pink-100', dot: 'bg-pink-500', text: 'text-pink-600', light: 'bg-pink-50' },
  paternity_leave: { bg: 'bg-indigo-100', dot: 'bg-indigo-500', text: 'text-indigo-600', light: 'bg-indigo-50' },
  unpaid_leave: { bg: 'bg-slate-100', dot: 'bg-slate-500', text: 'text-slate-600', light: 'bg-slate-50' },
  earned_leave: { bg: 'bg-emerald-100', dot: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50' },
};
const typeLabels = { sick_leave: 'Sick', casual_leave: 'Casual', vacation: 'Vacation', maternity_leave: 'Maternity', paternity_leave: 'Paternity', unpaid_leave: 'Unpaid', earned_leave: 'Earned', compensatory: 'Comp Off' };

export default function LeaveDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'school_admin' || user?.role === 'hr';
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [onLeaveToday, setOnLeaveToday] = useState([]);
  const [myBalance, setMyBalance] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [showApply, setShowApply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'casual_leave', start_date: '', end_date: '', reason: '' });
  const [dateValidation, setDateValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get(`/leave/team-calendar?month=${month}&year=${year}`).then(res => {
        setTeamLeaves(res.data.data.leaves);
        setOnLeaveToday(res.data.data.on_leave_today);
      }).catch(() => {});
    }
    api.get('/leave/balance').then(res => setMyBalance(res.data.data)).catch(() => {});
    api.get(`/leave/requests?user_id=${user?.user_id}&limit=5`).then(res => setMyRequests(res.data.data.requests)).catch(() => {});
  }, [month, year, isAdmin, user]);

  // Validate dates when they change
  useEffect(() => {
    if (leaveForm.start_date && leaveForm.end_date) {
      setValidating(true);
      api.post('/leave/validate-dates', { start_date: leaveForm.start_date, end_date: leaveForm.end_date })
        .then(res => setDateValidation(res.data.data))
        .catch(() => setDateValidation(null))
        .finally(() => setValidating(false));
    } else {
      setDateValidation(null);
    }
  }, [leaveForm.start_date, leaveForm.end_date]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (dateValidation && !dateValidation.valid) {
      toast.error(dateValidation.errors?.[0] || 'Invalid dates selected');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leave/requests', leaveForm);
      toast.success('Leave request submitted!');
      setShowApply(false);
      setLeaveForm({ leave_type: 'casual_leave', start_date: '', end_date: '', reason: '' });
      setDateValidation(null);
      api.get('/leave/balance').then(res => setMyBalance(res.data.data));
      api.get(`/leave/requests?user_id=${user?.user_id}&limit=5`).then(res => setMyRequests(res.data.data.requests));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m - 1, 1).getDay();
  const daysInMonth = getDaysInMonth(month, year);
  const firstDay = getFirstDayOfMonth(month, year);

  const getLeavesForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return teamLeaves.filter(lv => lv.start_date <= dateStr && lv.end_date >= dateStr);
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const statusIcon = (s) => {
    if (s === 'approved') return <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />;
    if (s === 'rejected') return <XCircle className="h-3.5 w-3.5 text-rose-500" />;
    return <Clock className="h-3.5 w-3.5 text-amber-500" />;
  };

  return (
    <div className="space-y-6" data-testid="leave-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-500/20">
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{t('leave_dashboard')}</h1>
            <p className="text-sm text-slate-500">{t('manage_track_leaves')}</p>
          </div>
        </div>
        <Button className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-md shadow-teal-500/20" onClick={() => setShowApply(true)} data-testid="apply-leave-btn">
          <Plus className="h-4 w-4 mr-2" /> {t('apply_for_leave')}
        </Button>
      </div>

      {/* Balance Cards */}
      {myBalance && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="balance-cards">
          {myBalance.balances?.map((bal) => {
            const lc = LEAVE_COLORS[bal.leave_type] || LEAVE_COLORS.casual_leave;
            const usedPct = bal.total_allocated > 0 ? ((bal.used / bal.total_allocated) * 100) : 0;
            return (
              <div key={bal.leave_type} className={`glass-card p-4 ${lc.light} border-transparent`} data-testid={`bal-${bal.leave_type}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${lc.dot}`} />
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{typeLabels[bal.leave_type] || bal.leave_type}</p>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-2xl font-bold text-slate-800">{bal.available}</span>
                  <span className="text-xs text-slate-400">/ {bal.total_allocated}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/80 overflow-hidden">
                  <div className={`h-full rounded-full ${lc.dot} transition-all`} style={{ width: `${Math.min(usedPct, 100)}%` }} />
                </div>
                <div className="flex justify-between mt-1.5 text-[10px] text-slate-400">
                  <span>Used: {bal.used}</span>
                  <span>Pending: {bal.pending}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Calendar */}
        {isAdmin && (
          <div className="lg:col-span-2 glass-card p-5" data-testid="team-calendar">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-800">{t('team_calendar')}</h2>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700" onClick={prevMonth}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-sm text-slate-600 font-medium min-w-[120px] text-center">{MONTHS[month - 1]} {year}</span>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700" onClick={nextMonth}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-xl overflow-hidden">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                <div key={d} className="text-center text-[10px] font-bold text-slate-400 py-2 bg-slate-50 uppercase tracking-wider">{d}</div>
              ))}
              {Array.from({ length: firstDay }, (_, i) => (
                <div key={`empty-${i}`} className="h-16 bg-white" />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dayLeaves = getLeavesForDay(day);
                const todayStr = now.toISOString().split('T')[0];
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isToday = dateStr === todayStr;
                const isWeekend = new Date(year, month - 1, day).getDay() % 6 === 0;
                return (
                  <div key={day} className={`h-16 p-1 bg-white border-t border-slate-100 ${isWeekend ? 'bg-slate-50/50' : ''} ${isToday ? 'ring-2 ring-teal-400 ring-inset' : ''}`}>
                    <span className={`text-xs ${isToday ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>{day}</span>
                    <div className="mt-0.5 space-y-0.5 overflow-hidden max-h-[36px]">
                      {dayLeaves.slice(0, 2).map((lv, j) => {
                        const lc = LEAVE_COLORS[lv.leave_type] || LEAVE_COLORS.casual_leave;
                        return (
                          <div key={j} className={`text-[8px] px-1 py-0.5 rounded ${lc.bg} ${lc.text} truncate font-medium`}>
                            {lv.user?.full_name?.split(' ')[0]}
                          </div>
                        );
                      })}
                      {dayLeaves.length > 2 && <div className="text-[8px] text-slate-300 px-1">+{dayLeaves.length - 2}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Right Sidebar */}
        <div className={`space-y-4 ${!isAdmin ? 'lg:col-span-3' : ''}`}>
          {/* On Leave Today */}
          {isAdmin && (
            <div className="glass-card p-5" data-testid="on-leave-today">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-bold text-slate-700">{t('on_leave_today')}</h3>
                <span className="ml-auto text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-bold">{onLeaveToday.length}</span>
              </div>
              {onLeaveToday.length === 0 ? (
                <p className="text-xs text-slate-400">{t('no_one_on_leave')}</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {onLeaveToday.map((lv, i) => {
                    const lc = LEAVE_COLORS[lv.leave_type] || LEAVE_COLORS.casual_leave;
                    return (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-xs font-bold text-white">
                          {lv.user?.full_name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-700 font-semibold truncate">{lv.user?.full_name}</p>
                          <p className="text-[10px] text-slate-400">{lv.user?.department || 'No dept'}</p>
                        </div>
                        <span className={`text-[10px] font-bold ${lc.text}`}>{typeLabels[lv.leave_type]}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Recent Requests */}
          <div className="glass-card p-5" data-testid="recent-requests">
            <h3 className="text-sm font-bold text-slate-700 mb-3">{t('my_recent_requests')}</h3>
            {myRequests.length === 0 ? (
              <p className="text-xs text-slate-400">{t('no_leave_requests')}</p>
            ) : (
              <div className="space-y-2">
                {myRequests.map((req) => {
                  const lc = LEAVE_COLORS[req.leave_type] || LEAVE_COLORS.casual_leave;
                  return (
                    <div key={req.request_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                      {statusIcon(req.status)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold ${lc.text}`}>{typeLabels[req.leave_type]}</span>
                          <span className="text-[10px] text-slate-400">{req.total_days}d</span>
                        </div>
                        <p className="text-[10px] text-slate-400">{req.start_date} to {req.end_date}</p>
                      </div>
                      <span className={`text-[10px] font-bold capitalize ${req.status === 'approved' ? 'text-emerald-500' : req.status === 'rejected' ? 'text-rose-500' : 'text-amber-500'}`}>{req.status}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apply Leave Dialog */}
      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="apply-leave-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800">{t('apply_for_leave')}</DialogTitle>
            <DialogDescription className="text-slate-500">{t('submit_request')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">{t('leave_type')}</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs font-semibold">{t('start_date')}</Label>
                <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} required className="bg-white border-slate-200 text-slate-800 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs font-semibold">{t('end_date')}</Label>
                <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} required className="bg-white border-slate-200 text-slate-800 rounded-xl" />
              </div>
            </div>
            {/* Date Validation Feedback */}
            {validating && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 text-xs text-slate-500">
                <div className="h-3 w-3 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" /> Validating dates...
              </div>
            )}
            {dateValidation && !validating && (
              <div className={`px-3 py-2.5 rounded-lg text-xs ${dateValidation.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="date-validation-msg">
                {dateValidation.errors?.map((e, i) => <p key={i} className="font-medium">{e}</p>)}
                {dateValidation.warnings?.map((w, i) => <p key={i} className="text-amber-600">{w}</p>)}
                {dateValidation.valid && <p className="font-medium">{dateValidation.working_days} working day{dateValidation.working_days !== 1 ? 's' : ''} will be deducted</p>}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-semibold">{t('reason')}</Label>
              <textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} required rows={3} className="w-full bg-white border border-slate-200 rounded-xl text-slate-800 text-sm p-3 placeholder:text-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10" placeholder={t('reason_placeholder')} />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold rounded-xl" disabled={submitting || (dateValidation && !dateValidation.valid)} data-testid="submit-leave-btn">
              {submitting ? t('submitting') : t('submit_request')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
